import { getApigeeBearerToken } from './apigeeService';
import { msalApp } from '../../auth';
import { chunk, MAX_SOURCE_IDS_PER_REQUEST } from '../utils/analyzedLogs';

export const fetchData = async (path) => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + path;
  const response = await fetch(REST_API_ENDPOINT);

  let data;
  try {
    data = await response.json();
  } catch (e) {
    return {
      statusCode: response.status,
      errorMessage: `Failed to parse JSON from ${REST_API_ENDPOINT}`
    };
  }

  return data;
};

export const postData = async (path, data) => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + path;

  try {
    const response = await fetch(REST_API_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data),
    });

    const responseBody = await response.json();

    return responseBody;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to post data to ${REST_API_ENDPOINT}: ${error.message}`
    };
  }
};


export const postFormData = async (path, formData) => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + path;

  try {
    const response = await fetch(REST_API_ENDPOINT, {
      method: "POST",
      body: formData
    });

    const responseBody = await response.json();

    return responseBody;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to post form data to ${REST_API_ENDPOINT}: ${error.message}`
    };
  }
};

export const patchData = async (path, data) => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + path;

  try {
    const response = await fetch(REST_API_ENDPOINT, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data),
    });

    const responseBody = await response.json();

    return responseBody;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to patch data to ${REST_API_ENDPOINT}: ${error.message}`
    };
  }
};

export const fetchOverviewData = async () => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/overview';
  
  try {
    const response = await fetch(REST_API_ENDPOINT);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to fetch overview data from ${REST_API_ENDPOINT}: ${error.message}`
    };
  }
};

export const fetchDeviceTimeline = async ({
  systemId,
  duration,
  page = 1,
  size = 50,
  sortBy = 'ts',
  sortDir = 'asc',
  eventsOnly = false
}) => {
  const params = new URLSearchParams({
    systemId,
    duration,
    page: page.toString(),
    size: size.toString(),
    sortBy,
    sortDir,
    eventsOnly: eventsOnly.toString()
  });

  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/device-timeline?${params}`;

  try {
    const response = await fetch(REST_API_ENDPOINT);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to fetch device timeline data from ${REST_API_ENDPOINT}: ${error.message}`
    };
  }
};

export const fetchDeviceProfileCrashAnalysis = async ({ systemId, startTimestamp, endTimestamp }) => {
  const params = new URLSearchParams({ systemId, startTimestamp, endTimestamp });
  const REST_API_ENDPOINT =
    process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/device-profile/crash-analysis?${params}`;

  const response = await fetch(REST_API_ENDPOINT);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Shared fetch for the Device Profile tab's live sections below. Throws (with
// `.status` set) on non-ok, same convention as fetchDeviceProfileCrashAnalysis.
async function fetchDeviceProfileJson(path, params) {
  const REST_API_ENDPOINT =
    process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/device-profile${path}?${new URLSearchParams(params)}`;

  const response = await fetch(REST_API_ENDPOINT);
  if (!response.ok) {
    const err = new Error(`HTTP error! status: ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export const fetchCurrentSessionState = ({ systemId, timestamp }) =>
  fetchDeviceProfileJson('/current-session-state', { systemId, timestamp });

export const fetchDeviceProfile = ({ systemId, referenceTimestamp }) =>
  fetchDeviceProfileJson('/device-profile', { systemId, referenceTimestamp });

export const fetchActivitySummary = ({ systemId, startTimestamp, endTimestamp, filter = 'ALL' }) =>
  fetchDeviceProfileJson('/activity-summary', { systemId, startTimestamp, endTimestamp, filter });

// Health Score API — owned by the Health Score team, but shares the same
// /api/v1/device-profile base path as the other endpoints above.
export const fetchHealthScore = ({ systemId, startTimestamp, endTimestamp }) =>
  fetchDeviceProfileJson('/health-score', { systemId, startTimestamp, endTimestamp });

export const fetchDeviceProfileUnifiedTimeline = async ({
  systemId,
  startTimestamp,
  endTimestamp,
  pageSize = -1,
  filter = 'ALL',
}) => {
  const params = new URLSearchParams({
    systemId,
    startTimestamp,
    endTimestamp,
    pageSize: pageSize.toString(),
    filter,
  });
  const REST_API_ENDPOINT =
    process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/device-profile/unified-timeline?${params}`;

  const response = await fetch(REST_API_ENDPOINT);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const fetchDeviceProfileDenseHealth = async ({ systemId, startTimestamp, endTimestamp }) => {
  const params = new URLSearchParams({ systemId, startTimestamp, endTimestamp });
  const REST_API_ENDPOINT =
    process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/device-profile/dense-data/health?${params}`;

  const response = await fetch(REST_API_ENDPOINT);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const fetchDeviceProfileSignalHistograms = async ({ systemId, startTimestamp, endTimestamp }) => {
  const params = new URLSearchParams({ systemId, startTimestamp, endTimestamp });
  const REST_API_ENDPOINT =
    process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/device-profile/dense-data/signal-histograms?${params}`;

  const response = await fetch(REST_API_ENDPOINT);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const fetchArtifactDownloadUrl = async ({ systemId, type, id }) => {
  const params = new URLSearchParams({ systemId, type, id: String(id) });
  const REST_API_ENDPOINT =
    process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/device-profile/artifact/download-url?${params}`;
  const response = await fetch(REST_API_ENDPOINT);
  if (!response.ok) {
    let reason = null;
    try {
      const body = await response.json();
      reason = body?.errorMessage ?? null;
    } catch (e) {}
    const err = new Error(reason || `HTTP error! status: ${response.status}`);
    err.status = response.status;
    err.reason = reason;
    throw err;
  }
  return response.json();
};

/**
 * Fetch all device makes for dropdown
 */
export const fetchDeviceMakes = async () => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/device-makes';
  try {
    const response = await fetch(REST_API_ENDPOINT);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch device makes: ${error.message}` };
  }
};

/**
 * Fetch all device models for dropdown
 */
export const fetchDeviceModels = async () => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/device-models';
  try {
    const response = await fetch(REST_API_ENDPOINT);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch device models: ${error.message}` };
  }
};

/**
 * Fetch all device operators for dropdown
 */
export const fetchDeviceOperators = async () => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/device-operators';
  try {
    const response = await fetch(REST_API_ENDPOINT);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch device operators: ${error.message}` };
  }
};

/**
 * Fetch all active cities for dropdown
 */
export const fetchCities = async () => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/cities';
  
  try {
    const response = await fetch(REST_API_ENDPOINT);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to fetch cities from ${REST_API_ENDPOINT}: ${error.message}`
    };
  }
};

/**
 * Fetch fleet summary with device status map for map rendering
 */
export const fetchFleetSummary = async (duration, cityId = 'all') => {
  const params = new URLSearchParams({
    duration,
    city_id: cityId
  });

  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/metrics/fleet-summary?${params}`;
  
  try {
    const response = await fetch(REST_API_ENDPOINT);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to fetch fleet summary from ${REST_API_ENDPOINT}: ${error.message}`
    };
  }
};

/**
 * Fetch device metrics with pagination
 */
export const fetchDeviceMetrics = async (duration, cityId = 'all', page = 1, pageSize = 10) => {
  const params = new URLSearchParams({
    duration,
    city_id: cityId,
    page: page.toString(),
    page_size: pageSize.toString()
  });

  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/metrics/devices?${params}`;
  
  try {
    const response = await fetch(REST_API_ENDPOINT);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to fetch device metrics from ${REST_API_ENDPOINT}: ${error.message}`
    };
  }
};

/**
 * Fetch onboarded devices with pagination
 */
export const fetchOnboardedDevices = async (cityId = 'all', status = 'ONBOARDED', page = 0, size = 20) => {
  const params = new URLSearchParams({
    city_id: cityId,
    status: status,
    page: page.toString(),
    size: size.toString()
  });

  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/devices/onboarded?${params}`;
  
  try {
    const response = await fetch(REST_API_ENDPOINT);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to fetch onboarded devices from ${REST_API_ENDPOINT}: ${error.message}`
    };
  }
};

/**
 * Onboard a single device
 */
export const onboardDevice = async (deviceData) => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/devices/onboard';
  
  try {
    const response = await fetch(REST_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(deviceData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to onboard device: ${error.message}`
    };
  }
};

/**
 * Deboard a device from the fleet
 */
export const deboardDevice = async (systemId, deboardedUser) => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/devices/deboard';
  try {
    const response = await fetch(REST_API_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ system_id: systemId, deboarded_user: deboardedUser || 'unknown' }),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to deboard device: ${error.message}` };
  }
};

/**
 * Bulk onboard multiple devices
 */
export const bulkOnboardDevices = async (bulkData) => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/devices/onboard/bulk';
  
  try {
    const response = await fetch(REST_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bulkData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to bulk onboard devices: ${error.message}`
    };
  }
};

/**
 * Fetch collector statistics
 */
export const fetchCollectorStatistics = async (duration) => {
  const REST_API_ENDPOINT = process.env.REACT_APP_WBC_API_BASE_URL +
    `/api/v1/collector-statistics?duration=${duration}`;

  try {
    const response = await fetch(REST_API_ENDPOINT);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      statusCode: 500,
      errorMessage: `Failed to fetch collector statistics: ${error.message}`
    };
  }
};

export const fetchFleetHealth = async (days = 7, cityId = 'ALL') => {
  const baseUrl = process.env.REACT_APP_WBC_API_BASE_URL;
  if (!baseUrl) return { statusCode: 500, errorMessage: 'REACT_APP_WBC_API_BASE_URL is not configured' };

  const range = days <= 1 ? '1d' : days <= 7 ? '7d' : '30d';
  const params = new URLSearchParams({ range, ...(cityId !== 'ALL' && { tag: cityId }) });
  const REST_API_ENDPOINT = baseUrl + `/queryService/api/v1/health/fleet?${params}`;

  try {
    const response = await fetch(REST_API_ENDPOINT);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: error.message };
  }
};

export const fetchFleetHealthTrend = async (days = 7, cityId = 'ALL') => {
  const baseUrl = process.env.REACT_APP_WBC_API_BASE_URL;
  if (!baseUrl) return { statusCode: 500, errorMessage: 'REACT_APP_WBC_API_BASE_URL is not configured' };

  const range = days <= 1 ? '1d' : days <= 7 ? '7d' : days <= 14 ? '14d' : '30d';
  const params = new URLSearchParams({ range, ...(cityId !== 'ALL' && { tag: cityId }) });
  const REST_API_ENDPOINT = baseUrl + `/queryService/api/v1/health/fleet/trend?${params}`;

  try {
    const response = await fetch(REST_API_ENDPOINT);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: error.message };
  }
};

// days is a plain integer here (not the '1d'/'7d'/'30d' range strings the sibling fleet
// endpoints use) — the UI has no day/week/month toggle yet; when one is added this call
// just needs a different `days` value, no backend change required.
export const fetchFleetHealthCounts = async (days = 7) => {
  const baseUrl = process.env.REACT_APP_WBC_API_BASE_URL;
  if (!baseUrl) return { statusCode: 500, errorMessage: 'REACT_APP_WBC_API_BASE_URL is not configured' };

  const params = new URLSearchParams({ days: String(days) });
  const REST_API_ENDPOINT = baseUrl + `/queryService/api/v1/health/fleet/counts?${params}`;

  try {
    const response = await fetch(REST_API_ENDPOINT);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: error.message };
  }
};

export const fetchFleetWidespreadIssues = async (days = 7) => {
  const baseUrl = process.env.REACT_APP_WBC_API_BASE_URL;
  if (!baseUrl) return { statusCode: 500, errorMessage: 'REACT_APP_WBC_API_BASE_URL is not configured' };

  const params = new URLSearchParams({ days: String(days) });
  const REST_API_ENDPOINT = baseUrl + `/queryService/api/v1/health/fleet/widespread-issues?${params}`;

  try {
    const response = await fetch(REST_API_ENDPOINT);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: error.message };
  }
};

export const fetchProblematicDevices = async (days = 7) => {
  const baseUrl = process.env.REACT_APP_WBC_API_BASE_URL;
  if (!baseUrl) return { statusCode: 500, errorMessage: 'REACT_APP_WBC_API_BASE_URL is not configured' };

  try {
    const response = await fetch(`${baseUrl}/queryService/api/v1/health/fleet/problematic-devices?days=${days}`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: error.message };
  }
};

export const fetchDeviceSnapshotMetadata = async (systemId) => {
  const baseUrl = process.env.REACT_APP_WBC_API_BASE_URL;
  if (!baseUrl) return { statusCode: 500, errorMessage: 'REACT_APP_WBC_API_BASE_URL is not configured' };

  try {
    const response = await fetch(`${baseUrl}/api/v1/devices/${encodeURIComponent(systemId)}/snapshot-metadata`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: error.message };
  }
};


export const fetchDeviceMetricTrend = async (systemId, days = 7) => {
  const baseUrl = process.env.REACT_APP_WBC_API_BASE_URL;
  if (!baseUrl) return { statusCode: 500, errorMessage: 'REACT_APP_WBC_API_BASE_URL is not configured' };

  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);

  const params = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
  });

  const REST_API_ENDPOINT = baseUrl + `/queryService/api/v1/health/devices/${encodeURIComponent(systemId)}/history?${params}`;

  try {
    const response = await fetch(REST_API_ENDPOINT);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: error.message };
  }
};

export const fetchDeviceHealthAssessment = async (systemId, days = 7, token = null) => {
  const baseUrl = process.env.REACT_APP_WBC_AI_INSIGHTS_URL;
  if (!baseUrl) return { statusCode: 500, errorMessage: 'REACT_APP_WBC_AI_INSIGHTS_URL is not configured' };

  const params = new URLSearchParams({ device_id: systemId, days: days.toString() });
  const REST_API_ENDPOINT = baseUrl + `/api/v2/insights/assessment?${params}`;

  try {
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(REST_API_ENDPOINT, { headers });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: error.message };
  }
};

// AI summary of the Device Profile unified event timeline. The caller POSTs the
// timeline events + reduced dense health it already fetched (so the expensive
// unified-timeline endpoint is not invoked again). Targets the AI Insights host
// via APIGEE bearer token, same transport as fetchDeviceHealthAssessment.
export const fetchDeviceProfileTimelineSummary = async ({
  token = null,
  deviceId,
  window,
  referenceTimestamp,
  timezone,
  events,
  denseSummary,
}) => {
  const baseUrl = process.env.REACT_APP_WBC_AI_INSIGHTS_URL;
  if (!baseUrl) return { statusCode: 500, errorMessage: 'REACT_APP_WBC_AI_INSIGHTS_URL is not configured' };

  const REST_API_ENDPOINT = baseUrl + '/api/v2/insights/timeline-summary';

  try {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(REST_API_ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        device_id: deviceId,
        window,
        reference_timestamp: referenceTimestamp,
        timezone,
        events,
        dense_summary: denseSummary,
      }),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: error.message };
  }
};

// ---------------------------------------------------------------------------
// RAG Document Management API
// ---------------------------------------------------------------------------

const RAG_BASE = () => process.env.REACT_APP_WBC_RAG_URL + (process.env.REACT_APP_WBC_RAG_SERVICE_PATH || '/wbcchatbot/rag/api/v1');

const getRagUserEmail = () => {
  try {
    const idToken = sessionStorage.getItem('cognitoIdToken');
    if (idToken) {
      const payload = JSON.parse(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      const email = payload.email || null;
      if (email) return email;
    }
  } catch { /* fall through to MSAL */ }
  try {
    const accounts = msalApp.getAllAccounts();
    if (accounts.length > 0) return accounts[0].username || null;
  } catch { return null; }
  return null;
};

const getRagAuthToken = async () => {
  const cognitoToken = sessionStorage.getItem('cognitoIdToken');
  if (cognitoToken) return cognitoToken;
  try {
    const accounts = msalApp.getAllAccounts();
    if (accounts.length > 0) {
      const response = await msalApp.acquireTokenSilent({
        scopes: ['email', 'openid', 'profile'],
        account: accounts[0],
      });
      return response.accessToken;
    }
  } catch { /* no MSAL token available */ }
  return null;
};

const ragFetch = async (url, options = {}) => {
  const userEmail = getRagUserEmail();
  if (userEmail) {
    options = { ...options, headers: { ...options.headers, 'X-User-Email': userEmail } };
  }
  const viaApigee = process.env.REACT_APP_WBC_RAG_VIA_APIGEE;
  const chars = [...(viaApigee || '')].map(c => `${c.charCodeAt(0)}(${c})`);
  console.log('[RAG] REACT_APP_WBC_RAG_VIA_APIGEE chars:', chars, '| ==="true":', viaApigee === 'true');
  if (viaApigee && viaApigee !== 'false') {
    console.log('[RAG] Fetching Apigee token...');
    const token = await getApigeeBearerToken();
    console.log('[RAG] Token acquired:', token ? token.substring(0, 20) + '...' : 'null');
    options = { ...options, headers: { ...options.headers, 'Authorization': `Bearer ${token}` } };
  }
  const userToken = await getRagAuthToken();
  if (userToken) {
    options = { ...options, headers: { ...options.headers, 'X-User-Token': userToken } };
  }
  return fetch(url, options);
};

// ---------------------------------------------------------------------------
// Client Debug API (which ODL / AD logs were analysed)
// ---------------------------------------------------------------------------

const getClientDebugUserEmail = (userEmail) => {
  if (typeof userEmail === 'string') return userEmail;
  if (userEmail && typeof userEmail === 'object') {
    return userEmail.mail || userEmail.email || getRagUserEmail();
  }
  return getRagUserEmail();
};

const clientDebugHeaders = (userEmail) => {
  const headers = {};
  const email = getClientDebugUserEmail(userEmail);
  if (email) headers['X-User-Email'] = email;

  const token = process.env.REACT_APP_CLIENT_DEBUG_API_TOKEN;
  if (typeof token === 'string' && token.trim()) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const fetchClientDebugJson = async (path, userEmail) => {
  const response = await fetch(process.env.REACT_APP_WBC_API_BASE_URL + path, {
    headers: clientDebugHeaders(userEmail),
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

/**
 * Fetch metadata for recent Client Debug jobs. The response intentionally contains job/run metadata only;
 * analysis output remains on-premises and is delivered through the result email.
 */
export const fetchClientDebugJobs = async ({ userEmail, limit = null } = {}) => {
  const email = getClientDebugUserEmail(userEmail);
  if (!email) return { jobs: [], errorMessage: 'No signed-in user' };
  const params = limit == null ? '' : `?${new URLSearchParams({ limit: String(limit) })}`;
  try {
    const jobs = await fetchClientDebugJson(`/api/v1/client-debug/jobs${params}`, email);
    return { jobs: Array.isArray(jobs) ? jobs : [] };
  } catch (error) {
    return { jobs: [], errorMessage: `Failed to fetch Client Debug jobs: ${error.message}` };
  }
};

/** Fetch safe, metadata-only aggregate KPIs for the selected 7/30/90-day range. */
export const fetchClientDebugAnalytics = async ({ userEmail, rangeDays = 30 } = {}) => {
  const email = getClientDebugUserEmail(userEmail);
  if (!email) return { analytics: {}, errorMessage: 'No signed-in user' };
  const selectedRange = [7, 30, 90].includes(Number(rangeDays)) ? Number(rangeDays) : 30;
  const params = new URLSearchParams({ rangeDays: String(selectedRange) });
  try {
    const analytics = await fetchClientDebugJson(`/api/v1/client-debug/analytics?${params}`, email);
    return { analytics: analytics && typeof analytics === 'object' && !Array.isArray(analytics) ? analytics : {} };
  } catch (error) {
    return { analytics: {}, errorMessage: `Failed to fetch Client Debug analytics: ${error.message}` };
  }
};

/**
 * Whether Client Debug accepts new debugs in this environment ({ enabled: true|false }). enabled is null when the
 * status could not be read, so callers show nothing rather than a wrong banner.
 */
export const fetchClientDebugStatus = async ({ userEmail } = {}) => {
  const email = getClientDebugUserEmail(userEmail);
  if (!email) return { enabled: null, errorMessage: 'No signed-in user' };
  try {
    const status = await fetchClientDebugJson('/api/v1/client-debug/status', email);
    return { enabled: typeof status?.enabled === 'boolean' ? status.enabled : null };
  } catch (error) {
    return { enabled: null, errorMessage: `Failed to fetch Client Debug status: ${error.message}` };
  }
};

/** Fetch read-only playbook metadata; prompts and worker-only fields stay server-side. */
export const fetchClientDebugPlaybooks = async ({ userEmail } = {}) => {
  const email = getClientDebugUserEmail(userEmail);
  if (!email) return { playbooks: [], errorMessage: 'No signed-in user' };
  try {
    const playbooks = await fetchClientDebugJson('/api/v1/client-debug/playbooks', email);
    return { playbooks: Array.isArray(playbooks) ? playbooks : [] };
  } catch (error) {
    return { playbooks: [], errorMessage: `Failed to fetch Client Debug playbooks: ${error.message}` };
  }
};

/**
 * Fetch the settings this environment runs Client Debug with (limits, retries, log pickup per playbook and where each
 * value came from) and the workers seen in the last day. Settings only: no credentials or storage locations.
 */
export const fetchClientDebugConfig = async ({ userEmail } = {}) => {
  const email = getClientDebugUserEmail(userEmail);
  if (!email) return { config: null, errorMessage: 'No signed-in user' };
  try {
    const config = await fetchClientDebugJson('/api/v1/client-debug/config', email);
    return { config: config && typeof config === 'object' && !Array.isArray(config) ? config : null };
  } catch (error) {
    return { config: null, errorMessage: `Failed to fetch the Client Debug configuration: ${error.message}` };
  }
};

/** Fetch one debug's lifecycle: status changes, stages with durations, email outcome, and the logs it picked up. */
export const fetchClientDebugJobTimeline = async ({ userEmail, jobId } = {}) => {
  const email = getClientDebugUserEmail(userEmail);
  if (!email) return { timeline: null, errorMessage: 'No signed-in user' };
  try {
    const timeline = await fetchClientDebugJson(`/api/v1/client-debug/jobs/${encodeURIComponent(jobId)}/timeline`, email);
    return { timeline: timeline && typeof timeline === 'object' ? timeline : null };
  } catch (error) {
    return { timeline: null, errorMessage: `Failed to fetch the debug timeline: ${error.message}` };
  }
};

/** Fetch everything Client Debug did for one device (newest first), each debug with its timeline and logs. */
export const fetchClientDebugDeviceTimeline = async ({ userEmail, deviceId, days = 30, limit = 25 } = {}) => {
  const email = getClientDebugUserEmail(userEmail);
  if (!email) return { device: null, errorMessage: 'No signed-in user' };
  if (!deviceId || !String(deviceId).trim()) return { device: null, errorMessage: 'Enter a device ID' };
  const params = new URLSearchParams({ days: String(days), limit: String(limit) });
  try {
    const device = await fetchClientDebugJson(
      `/api/v1/client-debug/devices/${encodeURIComponent(String(deviceId).trim())}/timeline?${params}`, email);
    return { device: device && typeof device === 'object' ? device : null };
  } catch (error) {
    return { device: null, errorMessage: `Failed to fetch the device timeline: ${error.message}` };
  }
};

/**
 * Which of the device's ODL / AD log files, given by the unified timeline's artifactId, were sent to an
 * analysis (LogTalk), with the job and analysis id. Identifiers only. Returns [] on any failure (no signed-in
 * user, network, non-OK): this only decorates the timeline and must never break it.
 */
export const fetchAnalyzedLogs = async ({ systemId, sourceIds }) => {
  const userEmail = getRagUserEmail();
  if (!userEmail || !systemId || !sourceIds || sourceIds.length === 0) return [];
  try {
    const responses = await Promise.all(chunk(sourceIds, MAX_SOURCE_IDS_PER_REQUEST).map(async (ids) => {
      const params = new URLSearchParams({ deviceId: systemId, sourceIds: ids.join(',') });
      const response = await fetch(
        process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/client-debug/analyzed-logs?${params}`,
        { headers: clientDebugHeaders(userEmail) });
      if (!response.ok) return [];
      const body = await response.json();
      return Array.isArray(body?.analyzedLogs) ? body.analyzedLogs : [];
    }));
    return responses.flat();
  } catch {
    return [];
  }
};

export const fetchRAGDocuments = async () => {
  try {
    const response = await ragFetch(`${RAG_BASE()}/rag/documents`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch RAG documents: ${error.message}` };
  }
};

export const uploadRAGDocument = async (file, documentType) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);
  try {
    const response = await ragFetch(`${RAG_BASE()}/rag/documents`, {
      method: 'POST',
      body: formData,
    });
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to upload document: ${error.message}` };
  }
};

export const replaceRAGDocument = async (docId, file, documentType) => {
  const formData = new FormData();
  formData.append('file', file);
  if (documentType) formData.append('document_type', documentType);
  try {
    const response = await ragFetch(`${RAG_BASE()}/rag/documents/${docId}`, {
      method: 'PUT',
      body: formData,
    });
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to replace document: ${error.message}` };
  }
};

export const updateRAGDocumentType = async (docId, documentType) => {
  try {
    const response = await ragFetch(`${RAG_BASE()}/rag/documents/${docId}/document-type`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_type: documentType }),
    });
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to update document type: ${error.message}` };
  }
};

export const deleteRAGDocument = async (docId) => {
  try {
    const response = await ragFetch(`${RAG_BASE()}/rag/documents/${docId}`, { method: 'DELETE' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to delete document: ${error.message}` };
  }
};

// ---------------------------------------------------------------------------
// Anomaly Detection (AD) APIs
// ---------------------------------------------------------------------------

/**
 * Fetch AD configurations. Optionally filter by anomaly-type `adId` (the human-
 * facing catalog id, e.g. 101 — NOT the configuration's internal `id`).
 * Response: array of `{ id, adId, label, description, technology, platform,
 *   status, handles[], stopTimerEnabled, ... }`
 */
export const fetchAdConfigurations = async (adId) => {
  const path = adId != null
    ? `/api/v1/ad-configurations?adId=${encodeURIComponent(adId)}`
    : '/api/v1/ad-configurations';
  try {
    const response = await fetch(process.env.REACT_APP_WBC_API_BASE_URL + path);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch AD configurations: ${error.message}` };
  }
};

/**
 * Fetch all AD Log Code Lists.
 * Response: array of `{ id, logCodesIndex, name, description, isDefault,
 *   logCodes[], createdBy, createdOn, updatedOn }`
 * `logCodesIndex` (not `id`) is what gets submitted in adCollectorConfigs rules.
 * Pre-select the entry where `isDefault === true`.
 */
export const fetchAdLogCodeLists = async () => {
  try {
    const response = await fetch(process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/ad-log-code-lists');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch AD log code lists: ${error.message}` };
  }
};

/**
 * Fetch the AD Anomaly Type catalog (the "Overall Anomaly" dropdown).
 * Response: array of `{ id, adId, name, description, technology, platform }`
 * Key on `adId` (human-facing catalog id, e.g. 101) — NOT `id`.
 */
export const fetchAdAnomalyTypes = async () => {
  try {
    const response = await fetch(process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/ad-anomaly-types');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch AD anomaly types: ${error.message}` };
  }
};

// ---------------------------------------------------------------------------
// AD Telemetry dashboard APIs
// ---------------------------------------------------------------------------

const adTelemetryQueryBase = () => {
  const host = process.env.REACT_APP_WBC_API_BASE_URL;
  if (!host) throw new Error('REACT_APP_WBC_API_BASE_URL is not configured');
  const configuredPath = process.env.REACT_APP_WBC_QUERY_SERVICE_PATH || '/queryService/api/v1';
  const queryPath = configuredPath.startsWith('/') ? configuredPath : `/${configuredPath}`;
  return `${host.replace(/\/$/, '')}${queryPath}/ad-telemetry`;
};

const adTelemetryRows = (rows, mapper) => Array.isArray(rows) ? rows.map(mapper) : [];
const adTelemetryNumber = (value) => value === null || value === undefined ? null : Number(value);

const mapAdTelemetryAnomaly = (row = {}) => ({
  anomalyId: row.anomaly_id,
  adId: row.ad_id,
  anomalyName: row.anomaly_name,
  occurrenceCount: adTelemetryNumber(row.occurrence_count) || 0,
});

const mapAdTelemetryFleetOverview = (data = {}) => ({
  startTime: data.startTime,
  endTime: data.endTime,
  summary: {
    totalAnomalies: adTelemetryNumber(data.summary?.total_anomalies) || 0,
    devicesAffected: adTelemetryNumber(data.summary?.devices_affected) || 0,
    loggingEnabledPercent: adTelemetryNumber(data.summary?.logging_enabled_percent) || 0,
    escalationRatePercent: adTelemetryNumber(data.summary?.escalation_rate_percent),
    escalationRateReason: data.summary?.escalation_rate_reason || null,
  },
  mostFrequent: data.mostFrequent && Object.keys(data.mostFrequent).length
    ? mapAdTelemetryAnomaly(data.mostFrequent) : null,
  topAnomalies: adTelemetryRows(data.topAnomalies, (row) => ({
    ...mapAdTelemetryAnomaly(row),
    technology: row.technology || null,
    loggingEnabledCount: adTelemetryNumber(row.logging_enabled_count) || 0,
    detectionOnlyCount: adTelemetryNumber(row.detection_only_count) || 0,
  })),
  dailyTrend: adTelemetryRows(data.dailyTrend, (row) => ({
    ...mapAdTelemetryAnomaly(row),
    day: row.day,
  })),
  loggingSplit: adTelemetryRows(data.loggingSplit, (row) => ({
    state: row.state,
    occurrenceCount: adTelemetryNumber(row.occurrence_count) || 0,
  })),
  platformDistribution: adTelemetryRows(data.platformDistribution, (row) => ({
    platform: row.platform,
    occurrenceCount: adTelemetryNumber(row.occurrence_count) || 0,
  })),
  topDevices: adTelemetryRows(data.topDevices, (row) => ({
    systemId: row.system_id,
    deviceId: row.device_id,
    occurrenceCount: adTelemetryNumber(row.occurrence_count) || 0,
  })),
  heatmap: adTelemetryRows(data.heatmap, (row) => ({
    ...mapAdTelemetryAnomaly(row),
    day: row.day,
  })),
});

const mapAdTelemetryAnomalyDevices = (data = {}) => ({
  summary: {
    distinctDevices: adTelemetryNumber(data.summary?.distinct_devices) || 0,
    totalOccurrences: adTelemetryNumber(data.summary?.total_occurrences) || 0,
    averagePerDevice: adTelemetryNumber(data.summary?.average_per_device) || 0,
    mtbaSeconds: adTelemetryNumber(data.summary?.mtba_seconds),
  },
  devices: adTelemetryRows(data.devices, (row) => ({
    systemId: row.system_id,
    deviceId: row.device_id,
    technology: row.technology || null,
    platform: row.platform || null,
    occurrenceCount: adTelemetryNumber(row.occurrence_count) || 0,
    lastOccurrence: row.last_occurrence || null,
    // Older gateways may still emit LOGGING_COMPLETE/LOGGING_IN_PROGRESS. Event detail is
    // intentionally binary: any non-detection state means logging was required.
    loggingState: row.logging_state === 'DETECTION_ONLY' ? 'DETECTION_ONLY' : 'LOGGING_ENABLED',
  })),
  page: adTelemetryNumber(data.page) || 0,
  pageSize: adTelemetryNumber(data.pageSize) || 50,
});

const mapAdTelemetryHistogram = (data = {}) => ({
  systemId: data.systemId,
  summary: {
    totalAnomalies: adTelemetryNumber(data.summary?.total_anomalies) || 0,
    distinctTypesHit: adTelemetryNumber(data.summary?.distinct_types_hit) || 0,
  },
  mostFrequent: data.mostFrequent && Object.keys(data.mostFrequent).length
    ? mapAdTelemetryAnomaly(data.mostFrequent) : null,
  distribution: adTelemetryRows(data.distribution, mapAdTelemetryAnomaly),
  newAnomalies: {
    anomalyTypeCount: adTelemetryNumber(data.newAnomalies?.anomaly_type_count) || 0,
  },
  recurring: {
    occurrenceCount: adTelemetryNumber(data.recurring?.occurrence_count) || 0,
  },
  newVsRecurring: adTelemetryRows(data.newVsRecurring, (row) => ({
    day: row.day,
    newOccurrenceCount: adTelemetryNumber(row.new_occurrence_count) || 0,
    recurringOccurrenceCount: adTelemetryNumber(row.recurring_occurrence_count) || 0,
  })),
  coOccurrence: adTelemetryRows(data.coOccurrence, (row) => ({
    anomalyIdLeft: row.anomaly_id_left,
    anomalyIdRight: row.anomaly_id_right,
    pairCount: adTelemetryNumber(row.pair_count) || 0,
  })),
});

const mapAdTelemetryEvents = (data = {}) => ({
  systemId: data.systemId,
  events: adTelemetryRows(data.events, (row) => ({
    eventTimestamp: row.event_timestamp,
    anomalyId: row.anomaly_id ?? row.ad_id,
    anomalyName: row.anomaly_name,
    source: row.source || null,
    deviceId: row.device_id || null,
    loggingState: row.logging_state || 'DETECTION_ONLY',
    logPath: row.log_path || null,
    artifacts: adTelemetryRows(row.artifacts, (artifact) => ({
      eventUuid: artifact.event_uuid || null,
      mergeJobId: artifact.merge_job_id || null,
      mergeSequence: adTelemetryNumber(artifact.merge_sequence),
      mergeStatus: artifact.merge_status || null,
      s3Uri: artifact.s3_uri || null,
      configurationResolutionStatus: artifact.configuration_resolution_status || null,
      configurationIds: String(artifact.configuration_ids || '').split(',').filter(Boolean),
    })),
  })),
  configuration: adTelemetryRows(data.configuration, (row) => ({
    anomalyId: row.anomaly_id,
    adId: row.ad_id,
    anomalyName: row.anomaly_name,
    configurationId: row.configuration_id,
    label: row.label || null,
    technology: row.technology || null,
    platform: row.platform || null,
    status: row.status || null,
    handleId: row.handle_id || null,
    triggerGroup: row.trigger_group || null,
    triggerClass: row.trigger_class || null,
    triggerName: row.trigger_name || null,
    classValues: row.class_values || null,
  })),
  page: adTelemetryNumber(data.page) || 0,
  pageSize: adTelemetryNumber(data.pageSize) || 50,
});

const fetchAdTelemetry = async (path, params, mapper, description) => {
  try {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') query.append(key, String(value));
    });
    const url = `${adTelemetryQueryBase()}${path}${query.size ? `?${query}` : ''}`;
    const response = await fetch(url);
    if (!response.ok) {
      let reason = `HTTP error! status: ${response.status}`;
      try {
        const body = await response.json();
        reason = body.error || reason;
      } catch (_) {
        // A non-JSON error still returns the HTTP status above.
      }
      throw new Error(reason);
    }
    return mapper(await response.json());
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch ${description}: ${error.message}` };
  }
};

export const fetchAdTelemetryFleetOverview = (filters) =>
  fetchAdTelemetry('/fleet-overview', filters, mapAdTelemetryFleetOverview, 'AD telemetry fleet overview');

export const fetchAdTelemetryAnomalyDevices = (anomalyId, filters) =>
  fetchAdTelemetry(`/anomalies/${encodeURIComponent(anomalyId)}/devices`, filters,
    mapAdTelemetryAnomalyDevices, 'AD telemetry anomaly devices');

export const fetchAdTelemetryDeviceHistogram = (systemId, filters) =>
  fetchAdTelemetry(`/devices/${encodeURIComponent(systemId)}/histogram`, filters,
    mapAdTelemetryHistogram, 'AD telemetry device histogram');

export const fetchAdTelemetryDeviceEvents = (systemId, filters) =>
  fetchAdTelemetry(`/devices/${encodeURIComponent(systemId)}/events`, filters,
    mapAdTelemetryEvents, 'AD telemetry device events');

/**
 * Fetch handle options for a given anomaly type and trigger group.
 * `adId`         — catalog adId (e.g. 101), NOT the internal id.
 * `triggerGroup` — "PRE" | "MAIN" | null (null = all groups in one call).
 * Response: array of `{ mappingId, handleMasterId, handleId, anomalyId,
 *   triggerGroup, triggerClass, triggerName, metric, scope,
 *   startCondition, stopCondition, defaultConfig, required }`
 * `triggerClass` (THRESHOLD/DROP/COUNT) tells the UI which classValues fields
 * to render. `handleMasterId` is submitted back in the create/update body.
 * When `triggerGroup` is null the full list is returned; split client-side.
 */
export const fetchAdHandleOptions = async (adId, triggerGroup) => {
  try {
    const base = `/api/v1/ad-anomaly-types/${encodeURIComponent(adId)}/handle-options`;
    const url = triggerGroup
      ? `${base}?triggerGroup=${encodeURIComponent(triggerGroup)}`
      : base;
    const response = await fetch(process.env.REACT_APP_WBC_API_BASE_URL + url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch AD handle options: ${error.message}` };
  }
};

/**
 * Get a single AD Configuration by its internal id.
 * Used for edit-mode pre-fill.
 * Response: `{ id, adId, label, description, technology, platform,
 *   status, stopTimerEnabled, stopTimerDuration, stopTimerUnit,
 *   stopTimerEvent, handles[] }`
 */
export const getAdConfiguration = async (id) => {
  try {
    const response = await fetch(
      process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/ad-configurations/${encodeURIComponent(id)}`
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch AD configuration: ${error.message}` };
  }
};

/**
 * Create a new AD Configuration.
 * `body` must match the AdConfigurationRequest contract:
 *   { label, adId, description, technology, platform, status,
 *     stopTimerEnabled, stopTimerDuration, stopTimerUnit, stopTimerEvent,
 *     handles: [{ handleMasterId, triggerGroup, classValues, enabled }] }
 * `user` — the operator's email/username, sent as `?user=` query param.
 * Response on success: `{ id, ... }` (id = the new configuration's primary key).
 */
export const createAdConfiguration = async (body, user) => {
  try {
    const url = process.env.REACT_APP_WBC_API_BASE_URL +
      `/api/v1/ad-configurations?user=${encodeURIComponent(user || '')}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to create AD configuration: ${error.message}` };
  }
};

/**
 * Full-replace update of an existing AD Configuration.
 * `id`   — the configuration's internal primary key.
 * `body` — same shape as createAdConfiguration; handles[] must include every
 *           handle you want to keep (full replace, not a patch).
 * `user` — operator email/username for the `?user=` query param.
 */
export const updateAdConfiguration = async (id, body, user) => {
  try {
    const url = process.env.REACT_APP_WBC_API_BASE_URL +
      `/api/v1/ad-configurations/${encodeURIComponent(id)}?user=${encodeURIComponent(user || '')}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to update AD configuration: ${error.message}` };
  }
};

// ---------------------------------------------------------------------------
// AD Log Code List management APIs
// ---------------------------------------------------------------------------

/**
 * Fetch the AD Log Code Catalog — the master set of ingested log codes that
 * backs the log-code picker when creating/editing a Log Code list.
 * Optional filters: `name` (substring, case-insensitive) and `logCode` (exact).
 * Response: array of `{ id, logCode, hexCode, name }`.
 */
export const fetchAdLogCodeCatalog = async (name, logCode) => {
  try {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    if (logCode !== undefined && logCode !== null && logCode !== '') {
      params.append('logCode', logCode);
    }
    const qs = params.toString();
    const url = process.env.REACT_APP_WBC_API_BASE_URL +
      `/api/v1/ad-log-code-catalog${qs ? `?${qs}` : ''}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch AD log code catalog: ${error.message}` };
  }
};

/**
 * Onboard a new log code into the AD Log Code Catalog.
 * `body`: `{ logCode, hexCode, name }`.
 * Response on success: `{ id, logCode, hexCode, name }`.
 */
export const createAdLogCodeCatalogEntry = async (body) => {
  try {
    const url = process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/ad-log-code-catalog';
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to onboard log code: ${error.message}` };
  }
};

/**
 * Get a single AD Log Code List by its internal id.
 * Response: `{ id, logCodesIndex, name, description, isDefault, logCodes[],
 *   createdBy, createdOn, updatedOn }`
 */
export const getAdLogCodeList = async (id) => {
  try {
    const response = await fetch(
      process.env.REACT_APP_WBC_API_BASE_URL + `/api/v1/ad-log-code-lists/${encodeURIComponent(id)}`
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to fetch AD log code list: ${error.message}` };
  }
};

/**
 * Create a new AD Log Code List.
 * `body`: `{ name, description, isDefault, logCodes[] }`
 * `logCodesIndex` is server-assigned (not part of the request body).
 * `logCodes` are plain decimal integers.
 * Response on success: `{ id, logCodesIndex, ... }`
 */
export const createAdLogCodeList = async (body, user) => {
  try {
    const url = process.env.REACT_APP_WBC_API_BASE_URL +
      `/api/v1/ad-log-code-lists?user=${encodeURIComponent(user || '')}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to create AD log code list: ${error.message}` };
  }
};

/**
 * Full-replace update of an AD Log Code List.
 * `logCodes[]` replaces the existing list entirely.
 */
export const updateAdLogCodeList = async (id, body, user) => {
  try {
    const url = process.env.REACT_APP_WBC_API_BASE_URL +
      `/api/v1/ad-log-code-lists/${encodeURIComponent(id)}?user=${encodeURIComponent(user || '')}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    return { statusCode: 500, errorMessage: `Failed to update AD log code list: ${error.message}` };
  }
};

export const fetchDeviceTags = async () => {
  try {
    const response = await fetch(process.env.REACT_APP_WBC_API_BASE_URL + '/api/v1/device-tags');
    if (!response.ok) return [];
    return await response.json();
  } catch {
    return [];
  }
};
